import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { usePageMeta } from "@/context/PageMetaContext";
import { countPendingCases, coverageCasePath, listCoverageCases } from "@/core/insurance/coverage-case-store";
import {
  createInsurerAgent,
  getInsurerById,
  listInsurerAgents,
} from "@/core/insurance/insurer-store";
import { insurerAgentFullName, type InsurerAgentInput } from "@/core/insurance/types";
import { COVERAGE_CASE_STATUS_LABELS } from "@/core/insurance/types";
import {
  getInsurerValidationRules,
  saveInsurerValidationRules,
  type InsurerValidationRules,
} from "@/core/insurance/insurer-rules-store";
import { getPatientById, listPatientsByInsurer, patientDetailPath } from "@/core/patients/patient-store";
import { patientFullName } from "@/core/patients/types";
import { routes } from "@/router/all_routes";

export function InsuranceInsurerDetailPage() {
  const { insurerId = "" } = useParams();
  const [refreshKey, setRefreshKey] = useState(0);
  const [agentForm, setAgentForm] = useState<Omit<InsurerAgentInput, "insurerId">>({
    ref: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });
  const [rulesForm, setRulesForm] = useState<InsurerValidationRules | null>(null);
  const [rulesMsg, setRulesMsg] = useState<string | null>(null);

  useEffect(() => {
    const refresh = () => setRefreshKey((k) => k + 1);
    window.addEventListener("hospitol:insurers", refresh);
    window.addEventListener("hospitol:coverage-cases", refresh);
    window.addEventListener("hospitol:insurer-rules", refresh);
    return () => {
      window.removeEventListener("hospitol:insurers", refresh);
      window.removeEventListener("hospitol:coverage-cases", refresh);
      window.removeEventListener("hospitol:insurer-rules", refresh);
    };
  }, []);

  useEffect(() => {
    if (insurerId) setRulesForm(getInsurerValidationRules(insurerId));
  }, [insurerId, refreshKey]);

  const insurer = useMemo(() => {
    void refreshKey;
    return getInsurerById(insurerId);
  }, [insurerId, refreshKey]);

  usePageMeta(
    insurer
      ? {
          pageTitle: insurer.name,
          breadcrumbLabel: insurer.name,
        }
      : null,
  );

  const agents = useMemo(() => {
    void refreshKey;
    return listInsurerAgents(insurerId);
  }, [insurerId, refreshKey]);

  const members = useMemo(() => {
    void refreshKey;
    return listPatientsByInsurer(insurerId);
  }, [insurerId, refreshKey]);

  const cases = useMemo(() => {
    void refreshKey;
    return listCoverageCases({ insurerId });
  }, [insurerId, refreshKey]);

  if (!insurer) {
    return (
      <div className="alert alert-warning">
        Assureur introuvable. <Link to={routes.insuranceInsurers}>Retour</Link>
      </div>
    );
  }

  const handleAddAgent = (e: FormEvent) => {
    e.preventDefault();
    if (!agentForm.ref.trim() || !agentForm.firstName.trim() || !agentForm.lastName.trim()) return;
    createInsurerAgent({ ...agentForm, insurerId });
    setAgentForm({ ref: "", firstName: "", lastName: "", email: "", phone: "" });
    setRefreshKey((k) => k + 1);
  };

  const handleSaveRules = (e: FormEvent) => {
    e.preventDefault();
    if (!rulesForm) return;
    saveInsurerValidationRules(rulesForm);
    setRulesMsg("Règles de validation enregistrées.");
    setRefreshKey((k) => k + 1);
    window.setTimeout(() => setRulesMsg(null), 2500);
  };

  return (
    <div>
      <PageHeader
        title={insurer.name}
        description={`Client CRM · ${insurer.code} · ${countPendingCases(insurerId)} dossier(s) en attente`}
        actions={
          <Link to={routes.insuranceInsurers} className="btn btn-outline-secondary btn-sm">
            Liste assureurs
          </Link>
        }
      />

      <div className="row g-3 mb-4 ph-split-workspace">
        <div className="col-lg-5">
          <div className="ph-invoice-sheet h-100">
            <h3 className="h6 fw-semibold mb-3">Fiche assureur</h3>
            <dl className="row small mb-0">
              <dt className="col-4 text-muted">Agrément</dt>
              <dd className="col-8">{insurer.licenseNumber || "—"}</dd>
              <dt className="col-4 text-muted">Contact</dt>
              <dd className="col-8">{insurer.contactPerson || "—"}</dd>
              <dt className="col-4 text-muted">Téléphone</dt>
              <dd className="col-8">{insurer.phone || "—"}</dd>
              <dt className="col-4 text-muted">E-mail</dt>
              <dd className="col-8">{insurer.email || "—"}</dd>
              <dt className="col-4 text-muted">Adresse</dt>
              <dd className="col-8">{insurer.address || "—"}</dd>
            </dl>
          </div>
        </div>
        <div className="col-lg-7">
          <div className="ph-invoice-sheet h-100">
            <h3 className="h6 fw-semibold mb-3">Agents en charge</h3>
            <form className="row g-2 mb-3" onSubmit={handleAddAgent}>
              <div className="col-12 col-sm-6 col-md-2">
                <input
                  className="form-control form-control-sm"
                  placeholder="Réf."
                  value={agentForm.ref}
                  onChange={(e) => setAgentForm((f) => ({ ...f, ref: e.target.value }))}
                />
              </div>
              <div className="col-12 col-sm-6 col-md-2">
                <input
                  className="form-control form-control-sm"
                  placeholder="Nom"
                  value={agentForm.lastName}
                  onChange={(e) => setAgentForm((f) => ({ ...f, lastName: e.target.value }))}
                />
              </div>
              <div className="col-12 col-sm-6 col-md-2">
                <input
                  className="form-control form-control-sm"
                  placeholder="Prénom"
                  value={agentForm.firstName}
                  onChange={(e) => setAgentForm((f) => ({ ...f, firstName: e.target.value }))}
                />
              </div>
              <div className="col-12 col-sm-6 col-md-3">
                <input
                  className="form-control form-control-sm"
                  placeholder="E-mail"
                  value={agentForm.email}
                  onChange={(e) => setAgentForm((f) => ({ ...f, email: e.target.value }))}
                />
              </div>
              <div className="col-12 col-sm-6 col-md-3">
                <button type="submit" className="btn btn-primary btn-sm w-100">
                  Ajouter agent
                </button>
              </div>
            </form>
            {agents.length === 0 ? (
              <p className="small text-muted mb-0">Aucun agent — ajoutez une référence de prise en charge.</p>
            ) : (
              <ul className="list-unstyled small mb-0">
                {agents.map((a) => (
                  <li key={a.id} className="mb-1">
                    <span className="ph-inv-badge me-1">{a.ref}</span>
                    {insurerAgentFullName(a)}
                    {a.email && <span className="text-muted"> — {a.email}</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      <div className="row g-3">
        <div className="col-lg-6">
          <div className="ph-invoice-sheet">
            <h3 className="h6 fw-semibold mb-3">Adhérents ({members.length})</h3>
            {members.length === 0 ? (
              <p className="small text-muted mb-0">Liez des patients à cet assureur depuis le registre.</p>
            ) : (
              <ul className="list-unstyled small mb-0">
                {members.map((p) => (
                  <li key={p.id} className="mb-1">
                    <Link to={patientDetailPath(p.id)}>{patientFullName(p)}</Link>
                    {p.memberNumber && (
                      <span className="text-muted"> — adh. {p.memberNumber}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <div className="col-lg-6">
          <div className="ph-invoice-sheet">
            <h3 className="h6 fw-semibold mb-3">Dossiers récents</h3>
            {cases.length === 0 ? (
              <p className="small text-muted mb-0">Aucun dossier de prise en charge.</p>
            ) : (
              <ul className="list-unstyled small mb-0">
                {cases.slice(0, 8).map((c) => {
                  const patient = getPatientById(c.patientId);
                  return (
                    <li key={c.id} className="mb-1">
                      <Link to={coverageCasePath(c.id)}>{c.label}</Link>
                      {patient && <span className="text-muted"> — {patientFullName(patient)}</span>}
                      <span className="badge bg-secondary ms-1">
                        {COVERAGE_CASE_STATUS_LABELS[c.status]}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>

      {rulesForm && (
        <div className="ph-invoice-sheet mb-4">
          <h3 className="h6 fw-semibold mb-2">Règles de validation (contrat)</h3>
          <p className="small text-muted mb-3">
            Seuls les actes correspondant à ces critères déclenchent une soumission au hub.
          </p>
          {rulesMsg && <div className="alert alert-success py-2">{rulesMsg}</div>}
          <form onSubmit={handleSaveRules}>
            <div className="row g-3 mb-3">
              <div className="col-12 col-sm-6 col-md-3">
                <div className="form-check">
                  <input
                    id="rules-enabled"
                    className="form-check-input"
                    type="checkbox"
                    checked={rulesForm.enabled}
                    onChange={(e) =>
                      setRulesForm((r) => (r ? { ...r, enabled: e.target.checked } : r))
                    }
                  />
                  <label className="form-check-label small" htmlFor="rules-enabled">
                    Contrôle actif
                  </label>
                </div>
              </div>
              <div className="col-12 col-sm-6 col-md-4">
                <label className="form-label small" htmlFor="rules-threshold">
                  Seuil montant (FC)
                </label>
                <input
                  id="rules-threshold"
                  type="number"
                  min={0}
                  className="form-control form-control-sm"
                  value={rulesForm.amountThreshold}
                  onChange={(e) =>
                    setRulesForm((r) =>
                      r ? { ...r, amountThreshold: Number(e.target.value) || 0 } : r,
                    )
                  }
                />
              </div>
            </div>
            <div className="row g-3 mb-3">
              <div className="col-12 col-md-6">
                <label className="form-label small" htmlFor="rules-aesthetic">
                  Mots-clés esthétique (virgules)
                </label>
                <input
                  id="rules-aesthetic"
                  className="form-control form-control-sm"
                  value={rulesForm.aestheticKeywords.join(", ")}
                  onChange={(e) =>
                    setRulesForm((r) =>
                      r
                        ? {
                            ...r,
                            aestheticKeywords: e.target.value
                              .split(",")
                              .map((k) => k.trim().toLowerCase())
                              .filter(Boolean),
                          }
                        : r,
                    )
                  }
                />
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label small" htmlFor="rules-prior">
                  Mots-clés accord préalable
                </label>
                <input
                  id="rules-prior"
                  className="form-control form-control-sm"
                  value={rulesForm.priorAuthKeywords.join(", ")}
                  onChange={(e) =>
                    setRulesForm((r) =>
                      r
                        ? {
                            ...r,
                            priorAuthKeywords: e.target.value
                              .split(",")
                              .map((k) => k.trim().toLowerCase())
                              .filter(Boolean),
                          }
                        : r,
                    )
                  }
                />
              </div>
            </div>
            <div className="row g-3 mb-3">
              <div className="col-12 col-md-6">
                <label className="form-label small" htmlFor="rules-meds">
                  Médicaments à valider sur ordonnance (virgules)
                </label>
                <input
                  id="rules-meds"
                  className="form-control form-control-sm"
                  placeholder="infliximab, sku-med-010"
                  value={rulesForm.priorAuthMedications.join(", ")}
                  onChange={(e) =>
                    setRulesForm((r) =>
                      r
                        ? {
                            ...r,
                            priorAuthMedications: e.target.value
                              .split(",")
                              .map((k) => k.trim().toLowerCase())
                              .filter(Boolean),
                          }
                        : r,
                    )
                  }
                />
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label small" htmlFor="rules-lab-blocked">
                  Analyses labo bloquées sans accord (virgules)
                </label>
                <input
                  id="rules-lab-blocked"
                  className="form-control form-control-sm"
                  placeholder="fertilité, fsh, amh, spermogramme"
                  value={rulesForm.blockedLabTests.join(", ")}
                  onChange={(e) =>
                    setRulesForm((r) =>
                      r
                        ? {
                            ...r,
                            blockedLabTests: e.target.value
                              .split(",")
                              .map((k) => k.trim().toLowerCase())
                              .filter(Boolean),
                          }
                        : r,
                    )
                  }
                />
              </div>
            </div>
            <div className="d-flex flex-wrap gap-3 mb-3 small">
              <label className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={rulesForm.validatePrescriptions}
                  onChange={(e) =>
                    setRulesForm((r) =>
                      r ? { ...r, validatePrescriptions: e.target.checked } : r,
                    )
                  }
                />
                Ordonnances
              </label>
              <label className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={rulesForm.validateConsultations}
                  onChange={(e) =>
                    setRulesForm((r) =>
                      r ? { ...r, validateConsultations: e.target.checked } : r,
                    )
                  }
                />
                Consultations
              </label>
              <label className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={rulesForm.validateLaboratory}
                  onChange={(e) =>
                    setRulesForm((r) =>
                      r ? { ...r, validateLaboratory: e.target.checked } : r,
                    )
                  }
                />
                Laboratoire
              </label>
              <label className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={rulesForm.validateImaging}
                  onChange={(e) =>
                    setRulesForm((r) =>
                      r ? { ...r, validateImaging: e.target.checked } : r,
                    )
                  }
                />
                Imagerie
              </label>
            </div>
            <button type="submit" className="btn btn-primary btn-sm">
              Enregistrer les règles
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
